import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage, then silently verify with server (10s timeout).
  // Falls back to localStorage data on timeout or network error; logs out on 401.
  useEffect(() => {
    const token     = localStorage.getItem('av_token');
    const savedUser = localStorage.getItem('av_user');
    let controller  = null;

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        logout();
        setLoading(false);
        return;
      }

      controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);

      api.get('/auth/me', { signal: controller.signal })
        .then(({ data }) => {
          clearTimeout(timer);
          localStorage.setItem('av_user', JSON.stringify(data.user));
          setUser(data.user);
        })
        .catch((err) => {
          clearTimeout(timer);
          if (err.response?.status === 401) logout();
          // timeout / network error → keep the localStorage user as-is
        });
    }

    setLoading(false);
    return () => { if (controller) controller.abort(); };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('av_token', data.token);
    localStorage.setItem('av_user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post('/auth/register', { name, email, password });
    localStorage.setItem('av_token', data.token);
    localStorage.setItem('av_user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  }, []);

  const googleLogin = useCallback(async (credential, accessToken) => {
    const payload = accessToken ? { access_token: accessToken } : { credential };
    const { data } = await api.post('/auth/google', payload);
    localStorage.setItem('av_token', data.token);
    localStorage.setItem('av_user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('av_token');
    localStorage.removeItem('av_user');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    localStorage.setItem('av_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, googleLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
